export async function fitImageToExactSizeIfNeeded(
  dataUrl: string,
  targetWidth: number,
  targetHeight: number,
  threshold = 0.015
): Promise<string> {
  const img = await loadImage(dataUrl);

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) return dataUrl;

  const targetAspect = targetWidth / targetHeight;
  const srcAspect = srcW / srcH;
  const aspectDelta = Math.abs(srcAspect - targetAspect) / targetAspect;

  // Already correct size and close enough aspect ratio
  if (srcW === targetWidth && srcH === targetHeight && aspectDelta < threshold) {
    return dataUrl;
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;

  // If aspect ratio is close, just rescale to exact pixels.
  if (aspectDelta < threshold) {
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    return canvas.toDataURL('image/png');
  }

  // Center-crop to target aspect ratio, then scale to exact size
  if (srcAspect > targetAspect) {
    // Source is wider than target — crop left/right
    const cropW = srcH * targetAspect;
    const cropX = (srcW - cropW) / 2;
    ctx.drawImage(img, cropX, 0, cropW, srcH, 0, 0, targetWidth, targetHeight);
  } else {
    // Source is taller than target — crop top/bottom
    const cropH = srcW / targetAspect;
    const cropY = (srcH - cropH) / 2;
    ctx.drawImage(img, 0, cropY, srcW, cropH, 0, 0, targetWidth, targetHeight);
  }

  return canvas.toDataURL('image/png');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}
